import { lazy, ComponentType, LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_FLAG = 'kertmagad_chunk_reload';

const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message)
  );
};

const readFlag = (): boolean => {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (value: boolean): void => {
  try {
    if (value) window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    else window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    // a tároló nem elérhető (privát mód) – nincs teendő
  }
};

/**
 * React.lazy burkoló: ha egy új deploy után a régi index.html-ből hivatkozott
 * chunk már nem létezik (ChunkLoadError), egyszer újratölti az oldalt, hogy a
 * friss index.html és chunkok töltődjenek be. A sessionStorage-jelző megakadályozza
 * a végtelen újratöltési ciklust.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  reload: () => void = () => window.location.reload()
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      writeFlag(false);
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && !readFlag()) {
        writeFlag(true);
        reload();
        // Az újratöltésig ne rendereljünk semmit (és ne dobjunk hibát)
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

export default lazyWithRetry;
